import { useState } from 'react';
import { Link } from 'react-router-dom';

const programIcons = {
  mern: (
    <svg viewBox="0 0 48 48" fill="none" className="h-8 w-8" aria-hidden="true">
      <path d="M8 36h32l3 4H5l3-4z" fill="currentColor" />
      <rect x="10" y="10" width="28" height="24" rx="2.5" stroke="currentColor" strokeWidth="2.4" />
      <path
        d="M20 18l-5 6 5 6M28 18l5 6-5 6"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  rupee: (
    <svg viewBox="0 0 48 48" fill="none" className="h-8 w-8" aria-hidden="true">
      <circle cx="24" cy="24" r="15" stroke="currentColor" strokeWidth="2.4" />
      <path
        d="M18 18h12M18 22h7.5a3.5 3.5 0 010 7H18M24 14v20M18 29h12"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  fullstack: (
    <svg viewBox="0 0 48 48" fill="none" className="h-8 w-8" aria-hidden="true">
      <rect x="8" y="10" width="32" height="22" rx="3" stroke="currentColor" strokeWidth="2.4" />
      <path d="M6 36h36" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <path
        d="M19 17l-5 6 5 6M29 17l5 6-5 6"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  graduation: (
    <svg viewBox="0 0 48 48" fill="none" className="h-8 w-8" aria-hidden="true">
      <path d="M6 20l18-9 18 9-18 9L6 20z" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
      <path d="M14 24v8c4 3 8 4.5 10 4.5S30 35 34 32v-8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M42 20v12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  ),
};

const tabs = [
  {
    id: 'launchpad',
    label: 'Career Launchpad',
    shortLabel: 'Launchpad',
    subtitle: '6-Month Professional Programs',
    programs: [
      { name: 'MERN Stack Development', iconSrc: '/images/programs/launchpad/mern.png', variant: 'badge' },
      { name: 'Data Science & Machine Learning', iconSrc: '/images/programs/launchpad/data-science.png', variant: 'badge' },
      { name: 'Finance', iconSrc: '/images/programs/launchpad/finance.png', variant: 'badge' },
      { name: 'HR & Management', iconSrc: '/images/programs/launchpad/hr.png', variant: 'badge' },
    ],
  },
  {
    id: 'flagship',
    label: 'Flagship Programs',
    shortLabel: 'Flagship',
    subtitle: '10-Month Programs · Partner Colleges',
    programs: [
      { name: 'AI-Powered Full Stack Program', iconSrc: '/images/programs/fullstack-visual.png', variant: 'visual' },
      { name: 'Finance & Accounting Program', iconSrc: '/images/programs/growth-chart.png', variant: 'visual' },
    ],
  },
  {
    id: 'cat',
    label: 'Super 51',
    shortLabel: 'Super 51',
    subtitle: 'MBA Career Accelerator',
    programs: [
      { name: 'Super 51 – MBA Career Accelerator', iconSrc: '/images/programs/super51-badge.png', variant: 'badge' },
    ],
  },
];

function ProgramCard({ name, icon, iconSrc, invertIcon, variant }) {
  return (
    <div className="group flex flex-col items-center rounded-2xl border border-brand-border bg-brand-white px-5 py-8 text-center shadow-[0_8px_24px_-18px_rgba(17,22,44,0.3)] transition-all duration-300 hover:-translate-y-1 hover:border-brand-primary/40 hover:shadow-md hover:shadow-brand-primary/10">
      {variant === 'badge' || variant === 'visual' ? (
        <div className="mb-5 flex h-20 w-20 items-center justify-center transition-transform duration-300 group-hover:scale-105 sm:h-24 sm:w-24">
          <img src={iconSrc} alt="" className="h-full w-full object-contain" aria-hidden="true" />
        </div>
      ) : (
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary text-brand-white transition-transform duration-300 group-hover:scale-105">
          {iconSrc ? (
            <img
              src={iconSrc}
              alt=""
              className={`h-8 w-8 object-contain ${invertIcon ? 'brightness-0 invert' : ''}`}
              aria-hidden="true"
            />
          ) : (
            programIcons[icon]
          )}
        </div>
      )}
      <p className="text-body-sm font-semibold leading-snug text-brand-navy sm:text-body">
        {name}
      </p>
    </div>
  );
}

function Programs() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const active = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];

  return (
    <section id="programs" className="relative overflow-hidden bg-brand-background py-6 lg:py-10">
      <div className="pointer-events-none absolute -right-24 top-20 h-72 w-72 rounded-full bg-brand-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute -left-16 bottom-10 h-64 w-64 rounded-full bg-brand-dark/5 blur-3xl" />

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="mx-auto mb-12 max-w-2xl text-center lg:mb-14">
          <p className="mb-3 text-label font-semibold uppercase tracking-[0.2em] text-brand-primary">
            Learn With Purpose
          </p>
          <h2 className="text-section uppercase tracking-wide text-brand-navy sm:text-[36px]">
            Programs We Offer
          </h2>
          <p className="mt-4 text-body-sm text-brand-secondary sm:text-body">
            Choose from Career Launchpad, Flagship Series, and Super 51 MBA Accelerator.
          </p>
          <div className="mx-auto mt-5 h-1 w-16 rounded-full bg-brand-primary" />
        </div>

        <div className="rounded-3xl border border-brand-border bg-brand-white p-5 shadow-[0_20px_60px_-30px_rgba(17,22,44,0.25)] sm:p-8 lg:p-10">
          <div
            role="tablist"
            aria-label="Program categories"
            className="mb-8 grid gap-3 sm:grid-cols-3"
          >
            {tabs.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  id={`program-tab-${tab.id}`}
                  aria-controls={`program-panel-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3.5 text-body-sm font-semibold transition-all ${
                    isActive
                      ? 'border-brand-primary bg-brand-primary text-brand-white shadow-md shadow-brand-primary/25'
                      : 'border-brand-border bg-brand-white text-brand-navy hover:border-brand-primary/40 hover:text-brand-primary'
                  }`}
                >
                  <span className="sm:hidden">{tab.shortLabel}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span
                    className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                      isActive
                        ? 'bg-brand-white/20 text-brand-white'
                        : 'bg-brand-background text-brand-primary'
                    }`}
                  >
                    {tab.programs.length}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            id={`program-panel-${active.id}`}
            aria-labelledby={`program-tab-${active.id}`}
          >
            <div className="mb-5 text-center sm:text-left">
              <h3 className="text-card-title text-brand-navy">{active.label}</h3>
              <p className="mt-1 text-label text-brand-secondary">{active.subtitle}</p>
            </div>

            <div
              className={`grid gap-4 ${
                active.programs.length === 1
                  ? 'mx-auto max-w-xs sm:grid-cols-1'
                  : active.programs.length === 2
                    ? 'sm:grid-cols-2'
                    : 'sm:grid-cols-2 xl:grid-cols-4'
              }`}
            >
              {active.programs.map((program) => (
                <ProgramCard key={program.name} {...program} />
              ))}
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              to="/programs"
              className="btn-primary inline-flex items-center gap-2 px-7"
            >
              View More
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Programs;
